'use client';

import { useRef, useEffect, useState } from 'react';
import { runWhenIdle } from '@/lib/utils';

// Fallback skills if Firestore is empty
const fallbackSkills = [
  'Web Design',
  'Mobile App Development',
  'UI/UX Design',
  'AI Engineering',
  'AI Agents and Integrations',
  'Tech Due Diligence',
  'Fractional CTO and Team Building',
  'MVP Mobile/Website',
];

export function SkillsSection() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLDivElement>(null);
  const listPinRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  const [skills, setSkills] = useState<string[]>(fallbackSkills);

  // Defer Firestore off the critical path — fallback renders instantly,
  // Firestore enriches on idle if data is available.
  useEffect(() => {
    let cancelled = false;

    runWhenIdle(async () => {
      try {
        const { getActiveServices } = await import('@/lib/services/serviceService');
        const services = await getActiveServices();
        if (!cancelled && services.length > 0) {
          setSkills(services.map((s) => s.title));
        }
      } catch (error) {
        console.error('Error fetching services:', error);
      }
    });

    return () => {
      cancelled = true;
    };
  }, []);

  // GSAP animations - run on mount (original behavior). GSAP is dynamic-
  // imported so the homepage entry chunk doesn't carry it (TASK-4).
  useEffect(() => {
    if (!sectionRef.current || !listPinRef.current || !listRef.current || !titleRef.current) return;

    const isMobile = window.innerWidth < 768;

    let ctx: { revert: () => void } | undefined;
    let cancelled = false;

    (async () => {
      const { gsap } = await import('@/lib/gsap');
      if (cancelled) return;

      ctx = gsap.context(() => {
      if (!isMobile) {
        // Pin the list while scrolling (Desktop)
        // Each item lights up individually as it scrolls into the viewport
        const listItems = listRef.current?.querySelectorAll('li');
        if (listItems) {
          listItems.forEach((item) => {
            gsap.fromTo(
              item,
              { opacity: 0.15 },
              {
                opacity: 1,
                duration: 0.6,
                ease: 'power2.out',
                scrollTrigger: {
                  trigger: item,
                  start: 'top 80%',
                  end: 'top 30%',
                  scrub: true,
                },
              }
            );
          });
        }

        // Hide the title on scroll
        gsap.to(titleRef.current, {
          scrollTrigger: {
            trigger: titleRef.current,
            start: 'top 50',
            end: 'top -50',
            scrub: true,
          },
          opacity: 0,
          y: -30,
        });
      } else {
        // Mobile simple scroll-in fade over time (no pinning, no scrub blocking)
        const listItems = listRef.current?.querySelectorAll('li');
        if (listItems) {
          listItems.forEach((item) => {
            gsap.fromTo(
              item,
              { opacity: 0.15 },
              {
                opacity: 1,
                duration: 0.8,
                scrollTrigger: {
                  trigger: item,
                  start: 'top 85%',
                  end: 'top 40%',
                  scrub: true, // Smooth scrub fade in as it scrolls up
                },
              }
            );
          });
        }
      }
    }, sectionRef);
    })();

    return () => {
      cancelled = true;
      ctx?.revert();
    };
  }, []);

  return (
    <section
      ref={sectionRef}
      className="relative z-30 overflow-hidden"
      // Translucent near-black: lets the ambient wing layer (z-[-1]) glow
      // through while still reading as a dark break from the teal.
      style={{ backgroundColor: 'rgba(12,12,12,0.65)', paddingTop: 'clamp(5rem, 8vw, 9rem)', paddingBottom: 'clamp(5rem, 8vw, 9rem)' }}
    >
      {/* Skills List Pin Container */}
      <div ref={listPinRef} className="relative">
        {/* OUR SKILLS COVER label */}
        <h2 className="sr-only">What we cover</h2>
        <div
          ref={titleRef}
          className="text-center"
          style={{ paddingBottom: 'clamp(2.5rem, 4vw, 4rem)' }}
        >
          <span className="eyebrow" aria-hidden="true">What we cover</span>
        </div>

        {/* Skills List */}
        <ul
          ref={listRef}
          className="flex flex-col items-center justify-center gap-1 px-6 text-center md:gap-3 md:px-8"
        >
          {skills.map((skill, index) => (
            <li
              key={`skill-${index}`}
              className="font-six-caps text-[calc(1rem+8vw)] uppercase leading-[0.95] text-white md:text-[calc(1rem+6vw)]"
            >
              {skill}
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
